import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  suppliersApi,
  Supplier,
  CreateSupplierDto,
  UpdateSupplierDto,
  GetSuppliersDto,
  SupplierStats,
  SupplierStatus,
  SupplierType,
} from '../api/suppliers';
import { getApiErrorMessage } from '../utils/errorHandling';

interface UseSuppliersReturn {
  // Data
  suppliers: Supplier[];
  statistics: SupplierStats | null;
  loading: boolean;
  error: string | null;
  
  // Operations
  createSupplier: (data: CreateSupplierDto) => Promise<Supplier | null>;
  updateSupplier: (id: string, data: UpdateSupplierDto) => Promise<Supplier | null>;
  deleteSupplier: (id: string) => Promise<boolean>;
  updateSupplierBalance: (id: string, amount: number) => Promise<Supplier | null>;
  refreshData: () => Promise<void>;
  
  // Utility functions
  getSupplierById: (id: string) => Supplier | undefined;
  getSupplierByCode: (code: string) => Supplier | undefined;
  getActiveSuppliers: () => Supplier[];
  getSuppliersByType: (type: SupplierType) => Supplier[];
  getSuppliersByStatus: (status: SupplierStatus) => Supplier[];
}

export const useSuppliers = (initialFilters: GetSuppliersDto = {}): UseSuppliersReturn => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [statistics, setStatistics] = useState<SupplierStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track current filters without causing dependency loops
  const currentFiltersRef = useRef(initialFilters);
  currentFiltersRef.current = initialFilters;

  // Load suppliers data - stable function reference
  const loadSuppliers = useCallback(async (filters: GetSuppliersDto = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // First, get the first page to see total count
      const firstResponse = await suppliersApi.getSuppliers({
        ...currentFiltersRef.current,
        ...filters,
        limit: 100,
        page: 1,
      });
      
      let allSuppliers = [...firstResponse.suppliers];
      
      // If there are more pages and we want all suppliers for local filtering
      if (firstResponse.totalPages > 1 && !filters.page) {
        const remainingPages = [];
        for (let page = 2; page <= firstResponse.totalPages; page++) {
          remainingPages.push(
            suppliersApi.getSuppliers({
              ...currentFiltersRef.current,
              ...filters,
              limit: 100,
              page,
            })
          );
        }
        
        // Fetch all remaining pages in parallel
        const remainingResponses = await Promise.all(remainingPages);
        remainingResponses.forEach(response => {
          allSuppliers.push(...response.suppliers);
        });
      }
      
      setSuppliers(allSuppliers);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to load suppliers');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []); // Remove initialFilters dependency

  // Load statistics - stable function reference
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await suppliersApi.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load supplier statistics:', err);
    }
  }, []);

  // Create stable function references for operations
  const loadStatisticsRef = useRef(loadStatistics);
  loadStatisticsRef.current = loadStatistics;

  // Create supplier
  const createSupplier = useCallback(async (data: CreateSupplierDto): Promise<Supplier | null> => {
    try {
      setLoading(true);
      const newSupplier = await suppliersApi.createSupplier(data);
      
      setSuppliers(prev => [newSupplier, ...prev]);
      toast.success('Supplier created successfully');
      
      // Refresh statistics
      loadStatisticsRef.current();
      
      return newSupplier;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to create supplier');
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update supplier
  const updateSupplier = useCallback(async (id: string, data: UpdateSupplierDto): Promise<Supplier | null> => {
    try {
      setLoading(true);
      const updatedSupplier = await suppliersApi.updateSupplier(id, data);
      
      setSuppliers(prev => 
        prev.map(supplier => 
          supplier.id === id ? updatedSupplier : supplier
        )
      );
      
      toast.success('Supplier updated successfully');
      
      // Refresh statistics
      loadStatisticsRef.current();
      
      return updatedSupplier;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to update supplier');
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete supplier
  const deleteSupplier = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      await suppliersApi.deleteSupplier(id);
      
      setSuppliers(prev => prev.filter(supplier => supplier.id !== id));
      toast.success('Supplier deleted successfully');
      
      // Refresh statistics
      loadStatisticsRef.current();
      
      return true;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to delete supplier');
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update supplier balance
  const updateSupplierBalance = useCallback(async (id: string, amount: number): Promise<Supplier | null> => {
    try {
      setLoading(true);
      const updatedSupplier = await suppliersApi.updateSupplierBalance(id, amount);
      
      setSuppliers(prev => 
        prev.map(supplier => 
          supplier.id === id ? updatedSupplier : supplier
        )
      );
      
      toast.success('Supplier balance updated successfully');
      
      // Refresh statistics
      loadStatisticsRef.current();
      
      return updatedSupplier;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to update supplier balance');
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create stable function references
  const loadSuppliersRef = useRef(loadSuppliers);
  loadSuppliersRef.current = loadSuppliers;

  // Refresh data - stable function reference
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadSuppliersRef.current(currentFiltersRef.current),
      loadStatisticsRef.current(),
    ]);
  }, []);

  // Utility functions
  const getSupplierById = useCallback((id: string): Supplier | undefined => {
    return suppliers.find(supplier => supplier.id === id);
  }, [suppliers]);

  const getSupplierByCode = useCallback((code: string): Supplier | undefined => {
    return suppliers.find(supplier => supplier.code === code);
  }, [suppliers]);

  const getActiveSuppliers = useCallback((): Supplier[] => {
    return suppliers.filter(supplier => supplier.status === SupplierStatus.ACTIVE);
  }, [suppliers]);

  const getSuppliersByType = useCallback((type: SupplierType): Supplier[] => {
    return suppliers.filter(supplier => supplier.type === type);
  }, [suppliers]);

  const getSuppliersByStatus = useCallback((status: SupplierStatus): Supplier[] => {
    return suppliers.filter(supplier => supplier.status === status);
  }, [suppliers]);

  // Load data on mount - only runs once
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadSuppliers(),
        loadStatistics(),
      ]);
    };
    
    loadInitialData();
  }, []); // Empty dependency array - only run on mount

  return {
    // Data
    suppliers,
    statistics,
    loading,
    error,
    
    // Operations
    createSupplier,
    updateSupplier,
    deleteSupplier,
    updateSupplierBalance,
    refreshData,
    
    // Utility functions
    getSupplierById,
    getSupplierByCode,
    getActiveSuppliers,
    getSuppliersByType,
    getSuppliersByStatus,
  };
}; 